import React from "react";
import { Helmet } from "react-helmet";

export default function PillReversal() {
  return (
    <div className="font-nunito bg-gradient-to-b from-pink-50/30 via-blue-50/20 to-purple-50/20">
      <Helmet>
        <link rel="icon" href="favicon.ico" />
        <title>Abortion Pill Reversal | Pathway Resource Center</title>
        <meta
          name="description"
          content="Time-sensitive support for abortion pill reversal. If you've taken the first dose and are having regrets, there may still be time. Call our 24/7 text line."
        />
      </Helmet>

      {/* Hero Section - Urgent but Compassionate */}
      <section className="relative overflow-hidden bg-gradient-to-br from-pink-100/40 via-purple-50/30 to-blue-50/40 py-16 md:py-24">
        {/* Soft Decorative Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute left-0 top-0 h-96 w-96 rounded-full bg-pink-200 blur-3xl"></div>
          <div className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-purple-200 blur-3xl"></div>
          <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-200 blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-4xl px-6 text-center">
          {/* Urgent Badge */}
          <div className="mb-6 flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-pink-600 px-6 py-3 text-white shadow-lg">
              <svg className="h-5 w-5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold uppercase tracking-wide">Time-Sensitive Support</span>
            </div>
          </div>

          <h1 className="mb-6 text-4xl font-semibold leading-tight text-gray-800 md:text-5xl lg:text-6xl">
            Abortion Pill Reversal
          </h1>

          <p className="mx-auto mb-8 max-w-2xl text-xl leading-relaxed text-gray-600 md:text-2xl">
            A Second Chance at Choice
          </p>

          {/* Immediate CTA */}
          <div className="mx-auto mb-8 max-w-xl">
            <a
              href="sms:+18707792229"
              className="group inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-pink-500 to-pink-600 px-8 py-5 text-xl font-bold text-white shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl sm:w-auto"
            >
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <div className="text-left">
                <div className="text-sm text-pink-100">24/7 Text Line</div>
                <div>870-779-2229</div>
              </div>
              <svg className="h-6 w-6 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>

          <div className="mx-auto max-w-3xl rounded-3xl bg-white/60 backdrop-blur-sm p-6 md:p-8 shadow-xl">
            <p className="text-lg leading-relaxed text-gray-700">
              If you've taken the first dose of the abortion pill and are having regrets,{" "}
              <span className="font-semibold text-pink-600">there may still be time</span>.
              Our compassionate team is here to help you 24/7.
            </p>
          </div>
        </div>

        {/* Gentle Wave Divider */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg
            viewBox="0 0 1440 120"
            className="w-full"
            preserveAspectRatio="none"
            style={{ height: '80px' }}
          >
            <path
              d="M0,64 C360,100 720,20 1440,64 L1440,120 L0,120 Z"
              fill="white"
              opacity="0.5"
            />
            <path
              d="M0,80 C360,40 720,100 1440,80 L1440,120 L0,120 Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Main Content Container */}
      <div className="mx-auto max-w-5xl px-6 py-12 md:py-16">

        {/* Understanding Section */}
        <section className="mb-16">
          <div className="overflow-hidden rounded-3xl bg-white shadow-lg">
            <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-8 md:p-12">
              <h2 className="mb-6 text-3xl font-semibold text-gray-800 md:text-4xl">
                We Understand
              </h2>

              <div className="space-y-6 text-lg leading-relaxed text-gray-700">
                <p>
                  At Pathway, we understand the overwhelming fear and stress that often
                  accompany an unplanned pregnancy, leading many women to make decisions
                  they later regret. However, we also recognize that circumstances can
                  change, and decisions can be reversed.
                </p>

                <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border-l-4 border-pink-500 p-6 shadow-sm">
                  <div className="flex items-start gap-3">
                    <svg className="h-6 w-6 text-pink-500 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold text-gray-800 mb-2">
                        If you've taken the first dose of the abortion pill and are having regrets,
                        there may still be time.
                      </p>
                      <p className="text-gray-700">
                        The abortion pill can potentially be reversed, but <span className="font-semibold text-pink-600">time is of the essence</span>.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="mb-16">
          <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-blue-50 to-purple-50 p-8 md:p-12 shadow-lg">
            <div className="mb-6 inline-block rounded-full bg-white/80 px-6 py-2 shadow-sm">
              <span className="text-sm font-semibold uppercase tracking-wide text-[#457B9D]">
                The Process
              </span>
            </div>

            <h2 className="mb-6 text-3xl font-semibold text-gray-800 md:text-4xl">
              How It Works
            </h2>

            <div className="space-y-6 text-lg leading-relaxed text-gray-700">
              <p>
                The abortion pill, also known as a chemical abortion, involves a two-step
                medication process. Following the administration of the first pill, some
                women experience regret and seek to reverse the process. This is where
                abortion pill reversal steps in.
              </p>

              <p>
                By utilizing the natural hormone progesterone, our team of medical
                professionals has successfully saved <span className="font-semibold text-[#457B9D]">64-68% of pregnancies</span> through
                abortion pill reversal. We are here to support you every step of the way.
              </p>

              {/* Steps */}
              <div className="mt-8 grid gap-6 sm:grid-cols-3">
                <div className="rounded-2xl bg-white/80 p-6 text-center shadow-md">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-pink-100 text-pink-600 font-bold text-xl">
                    1
                  </div>
                  <h3 className="mb-2 font-semibold text-gray-800">Reach Out</h3>
                  <p className="text-sm text-gray-600">
                    Contact us immediately via our 24/7 text line
                  </p>
                </div>
                <div className="rounded-2xl bg-white/80 p-6 text-center shadow-md">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 text-purple-600 font-bold text-xl">
                    2
                  </div>
                  <h3 className="mb-2 font-semibold text-gray-800">Consultation</h3>
                  <p className="text-sm text-gray-600">
                    Our medical team will assess your situation
                  </p>
                </div>
                <div className="rounded-2xl bg-white/80 p-6 text-center shadow-md">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-[#457B9D] font-bold text-xl">
                    3
                  </div>
                  <h3 className="mb-2 font-semibold text-gray-800">Treatment</h3>
                  <p className="text-sm text-gray-600">
                    Begin progesterone treatment if appropriate
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Emergency Contact Section */}
        <section className="mb-16">
          <div className="overflow-hidden rounded-3xl bg-white shadow-lg border-2 border-pink-200">
            <div className="bg-gradient-to-r from-pink-50 to-pink-100/50 p-8 md:p-12">
              <div className="flex items-start gap-4 mb-8">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-pink-500">
                  <svg className="h-7 w-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2 md:text-3xl">
                    Need Help Immediately?
                  </h2>
                  <p className="text-lg text-gray-700">
                    Don't hesitate to reach out if you're considering abortion pill reversal.
                    Our compassionate team is dedicated to providing you with the guidance
                    and support you need during this challenging time.
                  </p>
                </div>
              </div>

              {/* Primary CTA */}
              <div className="flex justify-center">
                <a
                  href="sms:+18707792229"
                  className="group inline-flex items-center justify-center gap-4 rounded-2xl bg-gradient-to-r from-pink-500 to-pink-600 px-10 py-5 text-xl font-bold text-white shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                >
                  <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <div className="text-left">
                    <div className="text-sm text-pink-100">24/7 Text Line</div>
                    <div className="text-2xl">870-779-2229</div>
                  </div>
                  <svg className="h-6 w-6 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>

              <p className="mt-6 text-center text-gray-600">
                Text us anytime, day or night. We're here for you.
              </p>
            </div>
          </div>
        </section>

        {/* Disclaimer Section */}
        <section className="mb-16">
          <div className="rounded-3xl bg-gradient-to-br from-gray-50 to-blue-50 p-8 md:p-12 shadow-md border-2 border-gray-200">
            <div className="flex items-start gap-4">
              <svg className="h-8 w-8 text-gray-500 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="mb-3 text-xl font-semibold text-gray-800">Disclaimer</h3>
                <p className="leading-relaxed text-gray-700">
                  Abortion Pill Reversal (APR) has demonstrated the ability to increase the
                  chances of allowing the pregnancy to continue. However, the outcome of your
                  specific reversal attempt cannot be guaranteed. Our team will work tirelessly
                  to assist you, but individual results may vary.
                </p>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* Footer Note */}
      <section className="bg-gradient-to-r from-pink-50 via-purple-50 to-blue-50 py-12">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <div className="rounded-2xl bg-white/80 backdrop-blur-sm p-6 shadow-lg">
            <p className="text-lg text-gray-700">
              <span className="font-semibold text-gray-800">Important Note:</span> Pathway does not provide abortions or abortion referrals.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
